/* @flow */
import * as React from 'react';
import { observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import { translate } from 'react-i18next';
import type { TranslatorProps } from 'react-i18next';

//----------------------------------------------------------------------//
// MATERIAL UI                                                          //
//----------------------------------------------------------------------//

import Button from '@material-ui/core/Button';
import Snackbar from '@material-ui/core/Snackbar';

//----------------------------------------------------------------------//
//----------------------------------------------------------------------//

import { browserHistory, apiService } from 'services/ServiceRegistry';

import CentralForm from 'components/CentralForm/CentralForm';
import { BoundTextField } from 'components/ExtendedUiElements/customized';
import { ComponentWithInjectedProps } from 'utils/injectedProps';
import UserStore from 'stores/UserStore';
import AuthenticationStore from 'stores/AuthenticationStore';
import { AccountModel } from 'models/AccountModels';

import styles from 'common-scss/formstyles.module.scss';

type InjectedProps = {
  accountModel: AccountModel,
  authenticationStore: AuthenticationStore,
  userStore: UserStore,
} & TranslatorProps;

@translate()
@inject('accountModel', 'authenticationStore', 'userStore')
@observer
export default class AccountNumbersForm extends ComponentWithInjectedProps<{}, {}, InjectedProps> {
  @observable localState = {
    formIsLoading: false,
    showResponseMessage: false,
    responseMessage: '',
  };

  handleSubmit = e => {
    const { accountModel, t } = this.props;

    const localState = this.localState;

    if (browserHistory.location.state) {
      const jsonObj = {
        company_name: browserHistory.location.state.companyName,
        tel: browserHistory.location.state.contactPhoneNo,
        country_id: browserHistory.location.state.country,
        state: browserHistory.location.state.stateCountryProvince,
        account_numbers: accountModel.accountNumbers,
        account_details: accountModel.additionalInfo,
      };

      apiService
        .createResource('user/additional-information', jsonObj)
        .then((response: Object) => {
          this.props.authenticationStore.logout();
          browserHistory.push('/welcome');
          return response;
        })

        .catch((err: string) => {
          localState.responseMessage = t('general:messages.request_error');
          localState.showResponseMessage = true;
        });
    } else {
      localState.responseMessage = t('general:messages.company_details_missing');
      localState.showResponseMessage = true;
      setTimeout(function() {
        browserHistory.push('/'); //After 3 second, redirect to step 1
      }, 3000);
    }

    e.preventDefault();
  };

  render(): React.Element<*> {
    const {
      accountModel,
      t,
      userStore: { isReadOnly },
    } = this.props;

    return (
      <React.Fragment>
        <CentralForm>
          <form onSubmit={this.handleSubmit}>
            <h1>
              {t('general:accounts.welcome', {
                companyName: process.env.REACT_APP_COMPANY_NAME,
              })}
            </h1>
            <p>
              {t('general:accounts.messages.lastly', {
                companyName: process.env.REACT_APP_COMPANY_NAME,
              })}
            </p>
            <h2>{t('general:accounts.step_2')}</h2>
            <h3>{t('general:actions.add_trading_account_plural')}</h3>

            <BoundTextField
              model={accountModel}
              prop="accountNumbers"
              floatingLabelText={t('general:trading_accounts:labels.numbers')}
              hintText={t('general:placeholders.start_typing')}
              helperText={t('general:accounts.found_on_invoice')}
            />
            <p>{t('general:accounts.messages.dont_know_account_numbers')}:</p>
            <p>{t('general:accounts.messages.provide_additional_info')}</p>
            <p>{t('general:accounts.messages.name_or_email')}</p>
            <BoundTextField
              model={accountModel}
              prop="additionalInfo"
              floatingLabelText={t('general:labels.additional_info')}
              multiline={true}
              rows={5}
            />
            <div className={styles['button-group']}>
              <Button color="primary" variant="contained" type="submit" disabled={isReadOnly}>
                {t('general:actions.submit')}
              </Button>
            </div>
            <br />
            <input type="submit" value="Skip" />
          </form>
        </CentralForm>
        <Snackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          open={this.localState.showResponseMessage}
          onClose={() => {
            this.localState.showResponseMessage = false;
          }}
          ContentProps={{
            'aria-describedby': 'message-id',
          }}
          message={<span id="message-id">{this.localState.responseMessage}</span>}
        />
      </React.Fragment>
    );
  }
}
